import React, { useEffect, useMemo, useState } from "react";
import { FiLayers, FiUploadCloud, FiX, FiVideo } from "react-icons/fi";
import Modal from "../../common/modal/Modal";
import AIDescriptionAssist from "../../common/ai/AIDescriptionAssist";
import { nameOf } from "../../../utils/aiDescription";
import {
  ACCEPT_ATTR,
  MAX_CATALOG_FILES,
  MAX_FILE_SIZE_MB,
  cleanText,
  getCatalogMedia,
  priceToInput,
  validateCatalogForm,
  validateMediaFile,
} from "./catalogUtils";

const FORM_ID = "catalog-form";

const emptyForm = { typeId: "", price: "", description: "", websiteUrl: "" };

const FieldError = ({ id, message }) =>
  message ? (
    <div id={id} className="text-danger small mt-1" role="alert">
      {message}
    </div>
  ) : null;

/**
 * Create / edit catalog form in the shared Modal.
 * `onSave({ id, typeId, price, description, websiteUrl, files })` resolves to { ok, error }.
 * The backend edit cannot remove images, so on edit the existing ones are shown read-only
 * and only newly added files are sent.
 */
const CatalogFormModal = ({ open, catalog, catalogTypes, onClose, onSave }) => {
  const isEdit = Boolean(catalog);
  const [form, setForm] = useState(emptyForm);
  const [files, setFiles] = useState([]);
  const [errors, setErrors] = useState({});
  const [mediaNotice, setMediaNotice] = useState("");
  const [submitError, setSubmitError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // Reset whenever the dialog opens for a (different) catalog
  useEffect(() => {
    if (!open) return;
    setForm(
      catalog
        ? {
            typeId: catalog.type?.id ? String(catalog.type.id) : "",
            price: priceToInput(catalog.price),
            description: cleanText(catalog.description),
            websiteUrl: cleanText(catalog.url),
          }
        : emptyForm
    );
    setFiles([]);
    setErrors({});
    setMediaNotice("");
    setSubmitError("");
  }, [open, catalog]);

  // Local previews, revoked when the selection changes
  const previews = useMemo(
    () => files.map((file) => ({ file, url: URL.createObjectURL(file), isVideo: file.type.startsWith("video/") })),
    [files]
  );
  useEffect(() => () => previews.forEach((item) => URL.revokeObjectURL(item.url)), [previews]);

  const existingMedia = useMemo(() => getCatalogMedia(catalog), [catalog]);

  const setField = (name) => (event) => {
    const value = name === "price" ? event.target.value.replace(/[^\d]/g, "") : event.target.value;
    setForm((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: undefined }));
  };

  const handleFiles = (event) => {
    const picked = Array.from(event.target.files || []);
    event.target.value = "";
    const accepted = [];
    const problems = [];
    picked.forEach((file) => {
      const problem = validateMediaFile(file);
      if (problem) problems.push(problem);
      else accepted.push(file);
    });
    const room = MAX_CATALOG_FILES - files.length;
    if (accepted.length > room) problems.push(`You can add up to ${MAX_CATALOG_FILES} files per save.`);
    setFiles((prev) => [...prev, ...accepted.slice(0, Math.max(room, 0))]);
    setMediaNotice(problems.join(" "));
    setErrors((prev) => ({ ...prev, media: undefined }));
  };

  const removeFile = (index) => setFiles((prev) => prev.filter((_, i) => i !== index));

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (submitting) return;
    const nextErrors = validateCatalogForm({ ...form, fileCount: files.length, isEdit });
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return;

    setSubmitting(true);
    setSubmitError("");
    const result = await onSave({ id: catalog?.id, ...form, files });
    setSubmitting(false);
    if (!result?.ok) setSubmitError(result?.error || "Something went wrong. Please try again.");
  };

  const noTypes = catalogTypes.length === 0;

  return (
    <Modal
      open={open}
      onClose={submitting ? undefined : onClose}
      title={isEdit ? "Edit catalog" : "Add catalog"}
      subtitle="Showcase a design project with its category, pricing and photos."
      icon={<FiLayers aria-hidden="true" />}
      size="lg"
      footer={
        <>
          <button type="button" className="btn btn-outline-secondary btn-sm" onClick={onClose} disabled={submitting}>
            Cancel
          </button>
          <button type="submit" form={FORM_ID} className="btn btn-primary btn-sm px-4" disabled={submitting}>
            {submitting ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" aria-hidden="true" />
                Saving…
              </>
            ) : isEdit ? (
              "Save changes"
            ) : (
              "Publish catalog"
            )}
          </button>
        </>
      }
    >
      <form id={FORM_ID} onSubmit={handleSubmit} noValidate>
        {submitError && (
          <div className="alert alert-danger py-2 small" role="alert">
            {submitError}
          </div>
        )}

        <div className="row g-3 mb-3">
          <div className="col-12 col-md-6">
            <label htmlFor="catalog-type" className="form-label fw-semibold small">
              Category <span className="text-danger" aria-hidden="true">*</span>
            </label>
            <select
              id="catalog-type"
              className={`form-select ${errors.typeId ? "is-invalid" : ""}`}
              value={form.typeId}
              onChange={setField("typeId")}
              disabled={noTypes}
              aria-required="true"
              aria-invalid={Boolean(errors.typeId)}
              aria-describedby={errors.typeId ? "catalog-type-error" : undefined}
            >
              <option value="">{noTypes ? "No categories available" : "Select category"}</option>
              {catalogTypes.map((type) => (
                <option key={type.id} value={type.id}>
                  {type.name}
                </option>
              ))}
            </select>
            <FieldError id="catalog-type-error" message={errors.typeId} />
          </div>

          <div className="col-12 col-md-6">
            <label htmlFor="catalog-price" className="form-label fw-semibold small">
              Estimated price (INR) <span className="text-danger" aria-hidden="true">*</span>
            </label>
            <input
              id="catalog-price"
              type="text"
              inputMode="numeric"
              className={`form-control ${errors.price ? "is-invalid" : ""}`}
              placeholder="e.g. 250000"
              value={form.price}
              onChange={setField("price")}
              aria-required="true"
              aria-invalid={Boolean(errors.price)}
              aria-describedby={errors.price ? "catalog-price-error" : undefined}
            />
            <FieldError id="catalog-price-error" message={errors.price} />
          </div>
        </div>

        <div className="mb-3">
          <label htmlFor="catalog-description" className="form-label fw-semibold small">
            Project description <span className="text-danger" aria-hidden="true">*</span>
          </label>
          <AIDescriptionAssist
            type="interior_design"
            values={{ categoryName: nameOf(catalogTypes, form.typeId), price: form.price }}
            value={form.description}
            onChange={(next) => {
              setForm((prev) => ({ ...prev, description: next }));
              setErrors((prev) => ({ ...prev, description: undefined }));
            }}
            showCounter
          >
            <textarea
              id="catalog-description"
              className={`form-control ${errors.description ? "is-invalid" : ""}`}
              rows={4}
              placeholder="Materials, design theme (e.g. Minimalist, Contemporary), scope of work…"
              value={form.description}
              onChange={setField("description")}
              aria-required="true"
              aria-invalid={Boolean(errors.description)}
              aria-describedby={errors.description ? "catalog-description-error" : undefined}
            />
          </AIDescriptionAssist>
          <FieldError id="catalog-description-error" message={errors.description} />
        </div>

        <div className="mb-3">
          <label htmlFor="catalog-url" className="form-label fw-semibold small">
            Project website or 3D walkthrough URL <span className="text-muted fw-normal">(optional)</span>
          </label>
          <input
            id="catalog-url"
            type="text"
            inputMode="url"
            className={`form-control ${errors.websiteUrl ? "is-invalid" : ""}`}
            placeholder="https://…"
            value={form.websiteUrl}
            onChange={setField("websiteUrl")}
            aria-invalid={Boolean(errors.websiteUrl)}
            aria-describedby={errors.websiteUrl ? "catalog-url-error" : undefined}
          />
          <FieldError id="catalog-url-error" message={errors.websiteUrl} />
        </div>

        <div className="mb-1">
          <label htmlFor="catalog-media" className="form-label fw-semibold small">
            Photos / videos {!isEdit && <span className="text-danger" aria-hidden="true">*</span>}
          </label>
          {isEdit && existingMedia.length > 0 && (
            <>
              <div className="d-flex gap-2 flex-wrap mb-2" aria-label="Current media">
                {existingMedia.map((item) =>
                  item.isVideo ? (
                    <span key={item.url} className="badge bg-secondary d-inline-flex align-items-center gap-1">
                      <FiVideo aria-hidden="true" /> Video
                    </span>
                  ) : (
                    <img key={item.url} src={item.url} alt="Current catalog" className="rounded object-fit-cover" style={{ width: 64, height: 64 }} />
                  )
                )}
              </div>
              <div className="form-text mb-2">Existing media is kept. New files you add below are appended.</div>
            </>
          )}
          <label
            htmlFor="catalog-media"
            className="d-flex flex-column align-items-center justify-content-center gap-1 border border-2 border-dashed rounded-3 p-3 text-center text-secondary small"
            style={{ cursor: "pointer" }}
          >
            <FiUploadCloud size={22} aria-hidden="true" />
            <span>Click to add files (up to {MAX_CATALOG_FILES}, max {MAX_FILE_SIZE_MB} MB each)</span>
            <span className="text-muted">JPG, PNG, WebP, GIF, MP4, MOV</span>
          </label>
          <input
            id="catalog-media"
            type="file"
            className="visually-hidden"
            multiple
            accept={ACCEPT_ATTR}
            onChange={handleFiles}
            aria-invalid={Boolean(errors.media)}
            aria-describedby={errors.media ? "catalog-media-error" : undefined}
          />
          <FieldError id="catalog-media-error" message={errors.media} />
          {mediaNotice && (
            <div className="text-warning-emphasis small mt-1" role="status">
              {mediaNotice}
            </div>
          )}

          {previews.length > 0 && (
            <ul className="list-unstyled d-flex gap-2 flex-wrap mt-3 mb-0">
              {previews.map((item, index) => (
                <li key={`${item.file.name}-${index}`} className="position-relative">
                  {item.isVideo ? (
                    <div className="rounded bg-dark text-white d-flex align-items-center justify-content-center" style={{ width: 84, height: 84 }}>
                      <FiVideo aria-hidden="true" />
                    </div>
                  ) : (
                    <img src={item.url} alt={item.file.name} className="rounded object-fit-cover" style={{ width: 84, height: 84 }} />
                  )}
                  <button
                    type="button"
                    className="btn btn-sm btn-light border rounded-circle position-absolute top-0 end-0 translate-middle-y p-0 d-flex align-items-center justify-content-center"
                    style={{ width: 22, height: 22 }}
                    onClick={() => removeFile(index)}
                    aria-label={`Remove ${item.file.name}`}
                    disabled={submitting}
                  >
                    <FiX size={12} aria-hidden="true" />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </form>
    </Modal>
  );
};

export default CatalogFormModal;
